<template>
  <div v-if="conference">
    <header class="page-header -bottom-spacing -sub-page">
      <section class="page-header__hgroup">
        <h1 v-appear:right="{ delay: [$scroll, 750] }" class="page-header__title">{{ conference.name }}</h1>
        <p v-appear="{ delay: [$scroll, 825, 75] }">{{ conference.description }}</p>
      </section>
    </header>

    <article v-if="hasBullets" v-appear="{ delay: [$scroll, 750] }" class="bg-wrap">
      <section class="layout-section grid-numbers">
        <div v-appear="{ delay: [$scroll, 825, 75] }" class="grid-numbers__item">
          <h3 class="grid-numbers__count">{{ conference.bullets.bullet1.info }}</h3>
          <h2 class="grid-numbers__label">{{ conference.bullets.bullet1.label }}</h2>
        </div>

        <div v-appear="{ delay: [$scroll, 900, 150] }" class="grid-numbers__item">
          <h3 class="grid-numbers__count">{{ conference.bullets.bullet2.info }}</h3>
          <h2 class="grid-numbers__label">{{ conference.bullets.bullet2.label }}</h2>
        </div>

        <div v-appear="{ delay: [$scroll, 975, 225] }" class="grid-numbers__item">
          <h3 class="grid-numbers__count">{{ conference.bullets.bullet3.info }}</h3>
          <h2 class="grid-numbers__label">{{ conference.bullets.bullet3.label }}</h2>
        </div>

        <div v-appear="{ delay: [$scroll, 1025, 350] }" class="grid-numbers__item">
          <h3 class="grid-numbers__count">{{ conference.bullets.bullet4.info }}</h3>
          <h2 class="grid-numbers__label">{{ conference.bullets.bullet4.label }}</h2>
        </div>
      </section>
    </article>

    <article class="layout-section -split -reverse">
      <figure
        v-appear:left="{ delay: [$scroll, 975, 25], threshold: 0 }"
        class="layout-section__figure -reverse"
        :style="{ '--image': `url('${conference.thumbnail}')` }"
      />

      <section class="layout-section__content">
        <div class="layout-section__hgroup">
          <span v-appear:right="{ delay: [$scroll, 950], threshold: 0.25 }" class="layout-section__toptitle">
            {{ conference.name }}
          </span>

          <h2 v-appear:right="{ delay: [$scroll, 1000, 50], threshold: 0 }" class="layout-section__title -no-bottom">
            {{ $t('common.ourPurpose') }}
          </h2>
        </div>

        <p v-appear="{ delay: 200 }">{{ conference.about }}</p>

        <a
          v-if="nextEvent && nextEvent.siteLink"
          v-appear="{ delay: 300 }"
          target="_blank"
          :href="nextEvent.siteLink"
          class="button layout-section__cta"
        >
          {{ $t('common.navigation.eventNext') }}
        </a>
      </section>
    </article>

    <div v-if="nextEvent" :id="`next-event-${slug}`" class="bg-wrap">
      <article class="layout-section -split">
        <figure
          v-appear:right
          class="layout-section__figure"
          :style="{ '--image': `url('${eventThumbFallback(slug, nextEvent.thumbnail)}')` }"
        />

        <section class="layout-section__content">
          <div class="layout-section__hgroup">
            <span v-appear:left class="layout-section__toptitle">{{ $t('common.nextEvent') }}</span>
            <h2 v-appear:left="{ delay: 100 }" class="layout-section__title -smaller -no-bottom">
              {{ nextEvent.title }}
            </h2>
          </div>

          <p v-appear="{ delay: 200 }">{{ limitChars(nextEvent.description, 800) }}</p>

          <a
            v-if="nextEvent.registerLink"
            v-appear="{ delay: 300 }"
            target="_blank"
            :href="nextEvent.registerLink"
            class="button layout-section__cta"
          >
            {{ $t('common.cta.registerNow') }}
          </a>
        </section>
      </article>
    </div>

    <section v-if="hasGallery" class="layout-section">
      <div class="layout-section__hgroup -flex -events">
        <h2 v-appear:right="{ delay: 100 }" class="layout-section__title -no-bottom">
          {{ $t('common.navigation.gallery') }}
        </h2>

        <LocaleLink v-appear="{ delay: 125 }" :to="{ name: 'conference-gallery', params: { slug } }" class="button">
          {{ $t('common.navigation.seeMore') }}
        </LocaleLink>
      </div>

      <article class="layout-grid -half -space-top">
        <div
          v-for="(image, i) in galleryPreview"
          :key="image"
          v-appear="{ delay: i * 50 + 100, threshold: 0 }"
          class="layout-grid__item"
        >
          <figure class="layout-grid__figure" :style="{ '--image': `url('${image}')` }" />
        </div>
      </article>
    </section>

    <article v-if="hasBoards" class="layout-section -split -reverse">
      <figure
        v-appear:left
        class="layout-section__figure -reverse"
        :style="{ '--image': `url('${conference.board.image}')` }"
      />

      <section class="layout-section__content">
        <div class="layout-section__hgroup">
          <span v-appear:right class="layout-section__toptitle">{{ $t('common.navigation.about') }}</span>
          <h2 v-appear:right="{ delay: 100 }" class="layout-section__title -no-bottom">{{ conference.board.title }}</h2>
        </div>

        <p v-appear="{ delay: 200 }">{{ conference.board.content }}</p>

        <div class="layout-section__cta">
          <LocaleLink
            v-if="conference.hasGovernors === 'true'"
            v-appear="{ delay: 300 }"
            :to="{ name: 'conference-board', params: { slug } }"
            class="button"
          >
            {{ getBoardLabel('boardOfGovernors') }}
          </LocaleLink>

          <LocaleLink
            v-if="conference.hasAdvisory === 'true'"
            v-appear="{ delay: 350 }"
            :to="{ name: 'conference-advisory', params: { slug } }"
            class="button"
          >
            {{ getBoardLabel('advisoryBoard') }}
          </LocaleLink>
        </div>
      </section>
    </article>

    <article v-if="archiveEvents.length" class="layout-section">
      <div class="layout-section__hgroup -flex -events">
        <h2 v-appear:right="{ delay: 100 }" class="layout-section__title -no-bottom">{{ $t('common.archives') }}</h2>
      </div>

      <section class="layout-grid -bigger">
        <div
          v-for="({ title, thumbnail, siteLink, overrideConference }, index) in archiveEvents"
          :key="title"
          v-appear="{ delay: index * 25 }"
          class="layout-grid__item"
        >
          <a
            v-if="siteLink"
            :href="siteLink"
            target="_blank"
            class="layout-grid__figure -small"
            :style="{ '--image': `url('${eventThumbFallback(slug, thumbnail)}')` }"
          />

          <div class="layout-grid__content">
            <h6 class="layout-grid__subtitle">{{ overrideConference || conference.name }}</h6>
            <h4 class="layout-grid__title">{{ title }}</h4>
          </div>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'Conference',
  props: {
    slug: { type: String, required: true }
  },
  metaInfo() {
    return {
      title: this.conference ? this.conference.name : ''
    }
  },
  computed: {
    conference() {
      return this.$i18n.messages[this.$i18n.locale].conferences[this.slug]
    },
    nextEvent() {
      return Array.isArray(this.conference.events)
        ? this.conference.events.find(({ startDate }) => moment(startDate).isAfter(moment()))
        : null
    },
    archiveEvents() {
      return Array.isArray(this.conference.events)
        ? this.conference.events.filter(
            ({ startDate, overrideConference }) => !overrideConference && moment(startDate).isBefore(moment())
          )
        : []
    },
    hasBullets() {
      return this.conference.hasBullets === 'true'
    },
    hasGallery() {
      return this.conference.hasGallery === 'true' && Array.isArray(this.conference.gallery)
    },
    hasFullGallery() {
      return this.hasGallery && this.conference.gallery.flatMap(({ images }) => images).length > 4
    },
    galleryPreview() {
      return this.conference.galleryPreview || []
    },
    hasBoards() {
      return this.conference.hasGovernors === 'true' || this.conference.hasAdvisory === 'true'
    }
  },
  created() {
    if (!this.conference) this.$router.replace({ name: `home-${this.$i18n.locale}` })
  },
  methods: {
    getBoardLabel(scope) {
      if (this.conference.hasGovernors === 'false' || this.conference.hasAdvisory === 'false') {
        return this.$t('common.navigation.seeMore')
      }

      return this.$t(`common.${scope}`)
    },
    limitChars(str, limit) {
      return str.length > limit ? `${str.substring(0, limit)}(...)` : str
    }
  }
}
</script>
